'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  FileText,
  FolderOpen,
  Image,
  Languages,
  Settings,
  Mail,
  MessageSquare,
  MessageCircle,
  Shield,
  HelpCircle,
  Search,
  BarChart3,
  Database,
  Monitor,
  FileCode,
  Package,
  Users,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  Home,
  Cpu,
  TestTube,
  Kanban,
  UserCircle,
  Building2,
  TrendingUp,
  Activity,
  Sparkles,
  Inbox,
  Phone,
  Workflow,
  Bell,
  KeyRound
} from 'lucide-react';
import {
  AI,
  ANALYTICS,
  BLOG,
  COMM,
  CRM,
  hasAnyPermission,
  INQUIRY,
  MARKETING,
  MEDIA_LIBRARY_NAVIGATION_PERMISSIONS,
  PRODUCT,
  SETTINGS,
  BACKUP,
  TRANSLATION,
} from '@/lib/permissions';

// 菜单项类型定义
interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  href?: string;
  permission?: string | string[];
  children?: MenuItem[];
  superAdminOnly?: boolean;
}

// ============================================================
// 菜单配置 - 权限映射说明
// ============================================================
// 权限格式: category.action (如 blog.post.view, inquiry.edit)
//
// 数据库权限分类:
// - blog: view, create, edit, delete, publish, translate, version
// - product: view, create, edit, delete, translate
// - category: view, create, edit, delete, translate
// - media: view, upload, delete, organize
// - inquiry: view, view_all, edit, edit_all, delete, assign, reply, export, statistics
// - ai_service: view, config, knowledge, history, analytics
// - config: llm, email, feishu, translation, protection, seo, cdn, analytics_tools
// - analytics: visitor, traffic, search, ip, geo
// - backup: view, create, restore, delete, cloud, schedule
// - system: user_view, user_create, user_edit, user_delete, permission, init, env
// ============================================================

const menuConfig: MenuItem[] = [
  // ========== 首页 ==========
  {
    id: 'dashboard',
    label: '仪表盘',
    icon: <Home size={20} />,
    href: '/admin-2025',
  },

  // ========== 内容管理模块 ==========
  {
    id: 'blog',
    label: '博客管理',
    icon: <FileText size={20} />,
    href: '/admin-2025/blog',
    permission: [BLOG.POST_VIEW, BLOG.POST_CREATE, BLOG.POST_EDIT, BLOG.POST_PUBLISH],
  },
  {
    id: 'products',
    label: '产品管理',
    icon: <Package size={20} />,
    href: '/admin-2025/products',
    permission: [PRODUCT.VIEW, PRODUCT.CREATE, PRODUCT.EDIT, PRODUCT.PUBLISH],
  },
  {
    id: 'media',
    label: '媒体库',
    icon: <Image size={20} />,
    href: '/admin-2025/media',
    permission: [...MEDIA_LIBRARY_NAVIGATION_PERMISSIONS],
  },
  {
    id: 'translation',
    label: '翻译管理',
    icon: <Languages size={20} />,
    href: '/admin-2025/translation',
    permission: [TRANSLATION.VIEW],
  },

  // ========== 业务运营模块 ==========
  {
    id: 'inquiries',
    label: '询盘管理',
    icon: <Mail size={20} />,
    href: '/admin-2025/inquiries',
    permission: [INQUIRY.VIEW_OWN, INQUIRY.VIEW_ALL],
  },
  {
    id: 'communications',
    label: '统一通讯',
    icon: <Inbox size={20} />,
    permission: [COMM.DASHBOARD_VIEW, COMM.EMAIL_USE_OWN, COMM.EMAIL_USE_ALL, COMM.WA_USE_OWN, COMM.WA_USE_ALL],
    children: [
      {
        id: 'comm-email',
        label: '邮箱',
        icon: <Mail size={18} />,
        permission: [COMM.EMAIL_USE_OWN, COMM.EMAIL_USE_ALL, COMM.EMAIL_MANAGE_OWN, COMM.EMAIL_MANAGE_ALL],
        children: [
          { id: 'comm-inbox', label: '统一邮箱', icon: <Inbox size={16} />, href: '/admin-2025/communications/inbox', permission: [COMM.EMAIL_USE_OWN, COMM.EMAIL_USE_ALL] },
          { id: 'comm-email-accounts', label: '账户管理', icon: <Settings size={16} />, href: '/admin-2025/communications/email', permission: [COMM.EMAIL_MANAGE_OWN, COMM.EMAIL_MANAGE_ALL] },
          { id: 'comm-tracking', label: '追踪统计', icon: <BarChart3 size={16} />, href: '/admin-2025/communications/tracking', permission: [COMM.TRACKING_VIEW] },
        ]
      },
      {
        id: 'comm-whatsapp',
        label: 'WhatsApp',
        icon: <MessageSquare size={18} />,
        permission: [COMM.WA_USE_OWN, COMM.WA_USE_ALL, COMM.WA_MANAGE_OWN, COMM.WA_MANAGE_ALL],
        children: [
          { id: 'comm-whatsapp-chats', label: '会话', icon: <MessageCircle size={16} />, href: '/admin-2025/communications/whatsapp/chats', permission: [COMM.WA_USE_OWN, COMM.WA_USE_ALL, COMM.WA_SEND] },
          { id: 'comm-whatsapp-manage', label: '实例管理', icon: <Phone size={16} />, href: '/admin-2025/communications/whatsapp/instances', permission: [COMM.WA_MANAGE_OWN, COMM.WA_MANAGE_ALL] },
        ]
      },
    ]
  },
  {
    id: 'crm',
    label: 'CRM系统',
    icon: <Kanban size={20} />,
    permission: [CRM.DASHBOARD_VIEW, CRM.CONTACT_VIEW_OWN, CRM.CONTACT_VIEW_ALL, CRM.COMPANY_VIEW_OWN, CRM.COMPANY_VIEW_ALL, CRM.DEAL_VIEW_OWN, CRM.DEAL_VIEW_ALL, CRM.ACTIVITY_VIEW_OWN, CRM.ACTIVITY_VIEW_ALL],
    children: [
      { id: 'crm-dashboard', label: '管理驾驶舱', icon: <BarChart3 size={18} />, href: '/admin-2025/crm/dashboard', permission: [CRM.DASHBOARD_VIEW] },
      { id: 'crm-sales-reps', label: '业务员管理', icon: <Users size={18} />, href: '/admin-2025/crm/sales-reps', permission: [CRM.DASHBOARD_VIEW] },
      { id: 'crm-board', label: '销售看板', icon: <Kanban size={18} />, href: '/admin-2025/crm', permission: [INQUIRY.VIEW_OWN, INQUIRY.VIEW_ALL] },
      { id: 'crm-contacts', label: '联系人', icon: <UserCircle size={18} />, href: '/admin-2025/crm/contacts', permission: [CRM.CONTACT_VIEW_OWN, CRM.CONTACT_VIEW_ALL] },
      { id: 'crm-companies', label: '公司', icon: <Building2 size={18} />, href: '/admin-2025/crm/companies', permission: [CRM.COMPANY_VIEW_OWN, CRM.COMPANY_VIEW_ALL] },
      { id: 'crm-deals', label: '商机', icon: <TrendingUp size={18} />, href: '/admin-2025/crm/deals', permission: [CRM.DEAL_VIEW_OWN, CRM.DEAL_VIEW_ALL] },
      { id: 'crm-activities', label: '活动记录', icon: <Activity size={18} />, href: '/admin-2025/crm/activities', permission: [CRM.ACTIVITY_VIEW_OWN, CRM.ACTIVITY_VIEW_ALL] },
    ]
  },
  {
    id: 'marketing',
    label: '营销工具',
    icon: <FolderOpen size={20} />,
    permission: [MARKETING.ASSET_VIEW, MARKETING.ASSET_UPLOAD, MARKETING.ASSET_EDIT],
    children: [
      { id: 'media-library', label: '素材库', icon: <Image size={18} />, href: '/admin-2025/marketing/media-library', permission: [MARKETING.ASSET_VIEW] },
    ]
  },

  // ========== 智能服务模块 ==========
  {
    id: 'ai-customer',
    label: 'AI客服',
    icon: <MessageSquare size={20} />,
    href: '/admin-2025/ai-customer-service',
    permission: [AI.CS_VIEW, AI.CS_MANAGE, AI.CS_KNOWLEDGE, AI.CS_HISTORY, AI.CS_ANALYTICS],
  },

  // ========== 数据分析模块 ==========
  {
    id: 'search-stats',
    label: '搜索统计',
    icon: <Search size={20} />,
    href: '/admin-2025/search-stats',
    permission: [ANALYTICS.SEARCH_VIEW],
  },
  {
    id: 'analytics',
    label: '访客分析',
    icon: <BarChart3 size={20} />,
    permission: [ANALYTICS.VIEW, ANALYTICS.SOURCE_VIEW, ANALYTICS.REGION_VIEW, ANALYTICS.LANDING_VIEW, ANALYTICS.ROI_VIEW, ANALYTICS.ATTRIBUTION_VIEW, ANALYTICS.POSTHOG_VIEW],
    children: [
      { id: 'analytics-source', label: '来源质量', icon: <TrendingUp size={18} />, href: '/admin-2025/analytics/source-quality', permission: [ANALYTICS.SOURCE_VIEW] },
      { id: 'analytics-region', label: '地区分析', icon: <Activity size={18} />, href: '/admin-2025/analytics/region-quality', permission: [ANALYTICS.REGION_VIEW] },
      { id: 'analytics-landing', label: '着陆页分析', icon: <FileCode size={18} />, href: '/admin-2025/analytics/landing-pages', permission: [ANALYTICS.LANDING_VIEW] },
      { id: 'analytics-roi', label: '广告成本 & ROI', icon: <TrendingUp size={18} />, href: '/admin-2025/analytics/ad-costs', permission: [ANALYTICS.ROI_VIEW] },
      { id: 'analytics-attribution', label: '多触点归因', icon: <Users size={18} />, href: '/admin-2025/analytics/attribution', permission: [ANALYTICS.ATTRIBUTION_VIEW] },
      { id: 'analytics-posthog', label: 'PostHog', icon: <Sparkles size={18} />, href: '/admin-2025/analytics/posthog', permission: [ANALYTICS.POSTHOG_VIEW] },
    ]
  },

  // ========== 系统管理模块 ==========
  {
    id: 'settings',
    label: '系统设置',
    icon: <Settings size={20} />,
    permission: [SETTINGS.SYSTEM_VIEW, SETTINGS.LLM_VIEW, SETTINGS.EMAIL_VIEW, SETTINGS.FEISHU_VIEW, SETTINGS.INTEGRATIONS_VIEW, SETTINGS.TEMPLATES_VIEW, SETTINGS.PROTECTION_VIEW, SETTINGS.RESPONSE_REMINDER_VIEW],
    children: [
      { id: 'system-config', label: '环境配置', icon: <Database size={18} />, href: '/admin-2025/system-config', permission: [SETTINGS.SYSTEM_VIEW] },
      { id: 'llm-config', label: 'LLM配置', icon: <Cpu size={18} />, href: '/admin-2025/settings/llm', permission: [SETTINGS.LLM_VIEW, SETTINGS.LLM_MANAGE] },
      { id: 'integrations', label: '工作流集成', icon: <Workflow size={18} />, href: '/admin-2025/settings/integrations', permission: [SETTINGS.INTEGRATIONS_VIEW, SETTINGS.INTEGRATIONS_MANAGE] },
      { id: 'email-config', label: '邮箱管理', icon: <Mail size={18} />, href: '/admin-2025/settings/email', permission: [SETTINGS.EMAIL_VIEW, SETTINGS.EMAIL_MANAGE] },
      { id: 'feishu-config', label: '飞书集成', icon: <MessageSquare size={18} />, href: '/admin-2025/settings/feishu', permission: [SETTINGS.FEISHU_VIEW, SETTINGS.FEISHU_MANAGE] },
      { id: 'auth-config', label: '认证设置', icon: <KeyRound size={18} />, href: '/admin-2025/settings/auth', permission: [SETTINGS.AUTH_MANAGE] },
      { id: 'response-reminder', label: '消息提醒', icon: <Bell size={18} />, href: '/admin-2025/settings/response-reminder', permission: [SETTINGS.RESPONSE_REMINDER_VIEW, SETTINGS.RESPONSE_REMINDER_MANAGE] },
      { id: 'notification-templates', label: '通知模板', icon: <FileText size={18} />, href: '/admin-2025/settings/templates', permission: [SETTINGS.TEMPLATES_VIEW, SETTINGS.TEMPLATES_MANAGE] },
      { id: 'content-protection', label: '内容保护', icon: <Shield size={18} />, href: '/admin-2025/settings/protection', permission: [SETTINGS.PROTECTION_VIEW, SETTINGS.PROTECTION_MANAGE] },
      { id: 'changelog', label: '更新日志', icon: <Sparkles size={18} />, href: '/admin-2025/settings/changelog', permission: [SETTINGS.CHANGELOG_VIEW] },
    ]
  },
  {
    id: 'backup',
    label: '数据备份',
    icon: <Database size={20} />,
    href: '/admin-2025/backup',
    permission: [BACKUP.VIEW, BACKUP.CREATE, BACKUP.RESTORE],
  },
  {
    id: 'users',
    label: '用户管理',
    icon: <Users size={20} />,
    href: '/admin-2025/users',
    superAdminOnly: true,  // 仅超级管理员可见
  },
];

interface AdminSidebarProps {
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

export function AdminSidebar({ collapsed = false, onCollapsedChange }: AdminSidebarProps) {
  const pathname = usePathname();
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  // 从 sessionStorage 获取用户信息
  useEffect(() => {
    const abortController = new AbortController();

    const loadUser = async () => {
      const userStr = sessionStorage.getItem('adminUser');
      if (userStr) {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
        await loadUserPermissions(user.id, abortController.signal);
      }
    };

    loadUser();

    return () => {
      abortController.abort();
    };
  }, []);

  // 加载用户权限
  const loadUserPermissions = async (userId: number, signal?: AbortSignal) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/permissions`, {
        credentials: 'include',
        signal
      });
      if (response.ok) {
        const data = await response.json();
        setUserPermissions(data.grantedPermissions || []);
      }
    } catch (error) {
      // 忽略因组件卸载导致的中断错误
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error('加载权限失败:', error);
    }
  };

  // 检查权限
  const hasPermission = (permission?: string | string[]): boolean => {
    if (!permission) return true;
    if (!currentUser) return false;
    if (currentUser.role === 'super_admin') return true;

    const requiredPermissions = Array.isArray(permission) ? permission : [permission];
    return hasAnyPermission(userPermissions, requiredPermissions);
  };

  // 检查菜单项是否可见
  const isMenuVisible = (item: MenuItem): boolean => {
    if (item.superAdminOnly && currentUser?.role !== 'super_admin') {
      return false;
    }
    return hasPermission(item.permission);
  };

  // 检查是否为当前活动路径
  const isActive = (href?: string): boolean => {
    if (!href) return false;
    const [path, query] = href.split('?');
    
    if (pathname === path || pathname?.startsWith(path + '/')) {
      if (query) {
        const currentQuery = new URLSearchParams(window.location.search);
        const targetQuery = new URLSearchParams(query);
        for (const [key, value] of targetQuery.entries()) {
          if (currentQuery.get(key) !== value) return false;
        }
      }
      return true;
    }
    return false;
  };

  // 检查子菜单是否有活动项
  const hasActiveChild = (item: MenuItem): boolean => {
    if (!item.children) return false;
    return item.children.some(child => isActive(child.href) || hasActiveChild(child));
  };

  // 切换菜单展开状态
  const toggleMenu = (id: string) => {
    setExpandedMenus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // 自动展开有活动子项的菜单
  useEffect(() => {
    menuConfig.forEach(item => {
      if (item.children && hasActiveChild(item)) {
        setExpandedMenus(prev => new Set(prev).add(item.id));
      }
    });
  }, [pathname]);

  // 渲染菜单项
  const renderMenuItem = (item: MenuItem, level: number = 0) => {
    if (!isMenuVisible(item)) return null;

    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus.has(item.id);
    const active = isActive(item.href) || hasActiveChild(item);

    // 根据层级计算缩进和样式
    const levelStyles: Record<number, string> = {
      0: '', // 一级菜单
      1: 'ml-4', // 二级菜单
      2: 'ml-8 border-l-2 border-gray-200', // 三级菜单，添加左边框
    };

    const itemContent = (
      <div
        className={`
          flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-200
          ${active
            ? 'bg-blue-50 text-blue-700 font-medium'
            : 'text-gray-700 hover:bg-gray-100'
          }
          ${level > 0 ? `${levelStyles[level] || levelStyles[2]} text-sm` : ''}
          ${level >= 2 ? 'py-2 text-xs' : ''}
          ${collapsed && level === 0 ? 'justify-center px-2' : ''}
        `}
        onClick={() => hasChildren && !collapsed && toggleMenu(item.id)}
      >
        <span className={`flex-shrink-0 ${active ? 'text-blue-600' : 'text-gray-500'} ${level >= 2 ? 'opacity-70' : ''}`}>
          {item.icon}
        </span>

        {!collapsed && (
          <>
            <span className="flex-1 truncate">{item.label}</span>
            {hasChildren && (
              <span className="text-gray-400">
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </span>
            )}
          </>
        )}
      </div>
    );

    return (
      <div key={item.id}>
        {item.href && !hasChildren ? (
          <Link href={item.href} onClick={() => setMobileOpen(false)}>
            {itemContent}
          </Link>
        ) : (
          itemContent
        )}

        {hasChildren && isExpanded && !collapsed && (
          <div className={`mt-1 space-y-0.5 ${level === 0 ? 'bg-gray-50/50 rounded-lg py-1 my-1' : ''}`}>
            {item.children!.map(child => renderMenuItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo/标题区域 */}
      <div className={`
        flex items-center h-16 px-4 border-b border-gray-200 flex-shrink-0
        ${collapsed ? 'justify-center' : 'justify-between'}
      `}>
        {!collapsed && (
          <span className="font-bold text-lg text-gray-900">管理后台</span>
        )}
        <button
          onClick={() => onCollapsedChange?.(!collapsed)}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hidden lg:block"
          title={collapsed ? '展开菜单' : '收起菜单'}
        >
          <Menu size={20} />
        </button>
      </div>

      {/* 菜单区域 */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {menuConfig.map(item => renderMenuItem(item))}
      </nav>

      {/* 底部用户信息 */}
      {currentUser && !collapsed && (
        <div className="border-t border-gray-200 p-4 flex-shrink-0">
          <div className="text-sm text-gray-600 truncate">
            {currentUser.username}
          </div>
          <div className="text-xs text-gray-400">
            {currentUser.role === 'super_admin' ? '超级管理员' : '管理员'}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* 移动端菜单按钮 */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-white shadow-md text-gray-700"
      >
        <Menu size={24} />
      </button>

      {/* 移动端遮罩 */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* 移动端侧边栏 */}
      <aside
        className={`
          lg:hidden fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl
          transform transition-transform duration-300
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <button
          onClick={() => setMobileOpen(false)}
          className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 text-gray-500"
        >
          <X size={20} />
        </button>
        {sidebarContent}
      </aside>

      {/* 桌面端侧边栏 */}
      <aside
        className={`
          hidden lg:block fixed inset-y-0 left-0 z-30 bg-white border-r border-gray-200
          transition-all duration-300
          ${collapsed ? 'w-16' : 'w-64'}
        `}
      >
        {sidebarContent}
      </aside>
    </>
  );
}
